import React, { useState } from 'react';
import useTranslation from '@app/hooks/use-translation';
import { Button, CircleButton, PageHeader, Paginator, Toggle } from '@ui';
import {
  useChangeStatusMFTaskMutation,
  useCloneMFTaskMutation,
  useDeleteMFTaskMutation,
  useGetMFTasksBulkQuery,
} from '@app/store/api/qm-module/qm-manage/qm-manage-monitoring-tasks.api';
import RubberTable from '@app/components/rubber-table';
import { TableColumn, TableItem } from '@app/components/rubber-table/table.types';
import AnalyticsStatus from '@app/components/ui/analytics-status/analytics-status';
import {
  MonitoringFormTasksBulkRequestType,
  MonitoringFormTasksStatusesType,
} from '@app/interfaces/qm-module/qm-manage/qm-monioring-tasks.type';
import { RemovalModal } from '@app/components/removal-modal';
import { PaginationResponse } from '@app/components/ui/paginator/paginator';
import { toast } from 'react-toastify';
import { TableFilter } from '@app/components/table/table.type';
import { useGetUserInfoQuery } from '@app/store/api/get-user-info.api';
import { navigationRoutes } from '@app/utils/navigation-routes';
import TableSkeleton from '@app/components/table/table-skeleton';

// eslint-disable-next-line
const statusesPossibleChanges: Record<
  MonitoringFormTasksStatusesType,
  Partial<MonitoringFormTasksStatusesType[]>
> = {
  PAUSE: ['ACTIVE', 'DONE'],
  NEW: ['STARTING'],
  ACTIVE: ['PAUSE', 'DONE'],
  STARTING: ['NEW'],
  DONE: [],
  PREPARE: [],
};

const toggleStatusChangeMap: Partial<
  Record<MonitoringFormTasksStatusesType, MonitoringFormTasksStatusesType>
> = {
  NEW: 'STARTING',
  ACTIVE: 'PAUSE',
  PAUSE: 'ACTIVE',
  STARTING: 'NEW',
};

const QmManageMonitoringTasks = () => {
  const { t } = useTranslation('pages.qmManage.monitoring_tasks');
  const { t: tStatuses } = useTranslation('components.reportsStatusLabel');

  const [mFTaskIdForDeleted, setMFTaskIdForDeleted] = useState<string>();
  const [mFTaskRequestData, setMFTaskRequestData] = useState<
    Omit<MonitoringFormTasksBulkRequestType, 'limit' | 'offset'>
  >({});
  const [paginationData, setPaginationData] = useState<PaginationResponse>({
    limit: 10,
    offset: 0,
  });
  // api
  const { data: MFTasksList, isLoading } = useGetMFTasksBulkQuery({
    ...paginationData,
    ...mFTaskRequestData,
  });

  const [changeMFTaskStatus] = useChangeStatusMFTaskMutation();
  const [deleteMFTaskObject] = useDeleteMFTaskMutation();
  const [cloneMFTask] = useCloneMFTaskMutation();
  const { data: userInfo } = useGetUserInfoQuery();
  // api

  function handleApproveDeleteTask() {
    if (!mFTaskIdForDeleted) return;
    deleteMFTaskObject({ id: mFTaskIdForDeleted });
  }

  function handleCloneTask(id: string) {
    cloneMFTask({ id });
  }

  function handleChangeStatus(status: MonitoringFormTasksStatusesType, id: string) {
    changeMFTaskStatus({ status, id })
      .unwrap()
      .then((data) => {
        toast.success(
          `${t('status.status_will_be_changed')} "${tStatuses(status.toLowerCase())}"
              ${t('status.to')}
              "${tStatuses(data.status.toLowerCase())}"`,
        );
      })
      .catch(() => {
        toast.info(t('status.change_status_failed'));
      });
  }

  // table
  const tableSortKeyList = {
    dt_start: 'start',
    dt_end: 'end',
    name: 'name',
  };
  function handleChangeTableFilter(filter: TableFilter) {
    const keys = Object.entries(filter).flat(Infinity);
    setMFTaskRequestData((prev) => ({
      ...prev,
      sortBy: tableSortKeyList[keys[0] as string],
      sortDesc: keys.length ? 0 < (keys[1] as number) : undefined,
    }));
  }

  function handleToggleStatus(status: MonitoringFormTasksStatusesType, id: string) {
    const statusToChange = toggleStatusChangeMap?.[status];
    if (!statusToChange) return;
    handleChangeStatus(statusToChange, id);
  }

  const columns: TableColumn[] = [
    { index: 'name', title: t('table.name'), filter: true },
    { index: 'dt_start', title: t('table.dt_start'), filter: true, maxWidth: 180 },
    { index: 'dt_end', title: t('table.dt_end'), filter: true, maxWidth: 180 },
    { index: 'status', title: t('table.status'), maxWidth: 140 },
    { index: 'fastTravel', title: '', maxWidth: 150 },
    { index: 'progress', title: t('table.progress'), maxWidth: 150 },
    { index: 'actions', title: t('table.actions'), maxWidth: 160 },
  ];

  const tableData: TableItem[] =
    MFTasksList?.items?.map((task) => ({
      name: task.name,
      dt_start: task.dt_start?.split(' ')?.[0],
      dt_end: task.dt_end?.split(' ')?.[0],
      status: (
        <div className="flex items-center justify-between">
          <div className="w-fit">
            <AnalyticsStatus
              qmMode
              additionalStatusList={{ NEW: { title: t('new_status') } }}
              status={task.status}
            />
          </div>
          <div>
            <Toggle
              checked={['STARTING', 'ACTIVE'].includes(task.status)}
              disabled={
                !['NEW', 'ACTIVE', 'PAUSE', 'STARTING'].includes(task.status) ||
                userInfo?.qm_role !== 'manager'
              }
              onChecked={() => handleToggleStatus(task.status, task.id)}
            />
          </div>
        </div>
      ),
      progress: <div>{task.progress}</div>,
      fastTravel: (
        <div className="w-full flex items-center">
          <Button
            link={`./${task.id}/${navigationRoutes.qm_manage_monitoring_tasks_monitoring}`}
            fill="linked"
            label={t('tabs.monitoring')}
          />
        </div>
      ),
      actions: (
        <div className="flex items-center gap-[5px]">
          <CircleButton
            hintTitle={t('table.hints.settings')}
            icon="SettingIcon"
            size={18}
            link={
              userInfo?.qm_role === 'controller'
                ? `${task.id}/${navigationRoutes.qm_manage_monitoring_tasks_monitoring}`
                : task.id
            }
            disable={userInfo?.qm_role !== 'manager'}
            className="hover:text-action"
          />
          <CircleButton
            hintTitle={t('table.hints.copy')}
            icon="CopyIcon"
            onClick={() => handleCloneTask(task.id)}
            size={18}
            className="hover:text-action"
            disable={userInfo?.qm_role !== 'manager'}
          />
          <CircleButton
            hintTitle={t('table.hints.delete')}
            disable={task.status !== 'NEW' || userInfo?.qm_role !== 'manager'}
            icon="TrashIcon"
            onClick={() => setMFTaskIdForDeleted(task.id)}
            size={18}
            className="text-basic_red"
          />
          <CircleButton
            hintTitle={t('table.hints.complete')}
            icon="CheckIcon"
            disable={
              userInfo?.qm_role === 'manager'
                ? task.status !== 'ACTIVE' && task.status !== 'PAUSE'
                : true
            }
            size={17}
            onClick={() => handleChangeStatus('DONE', task.id)}
            className="text-basic_green"
          />
        </div>
      ),
    })) || [];
  // table

  return (
    <div>
      <PageHeader label={t('page_title')} icon="DocInDocIcon">
        {userInfo?.qm_role !== 'controller' && (
          <Button label={t('add_task_button')} link={'new'} icon="CirclePlusIcon" />
        )}
      </PageHeader>
      <div className="flex flex-col gap-[20px]">
        {isLoading ? (
          <TableSkeleton count={10} />
        ) : (
          <RubberTable
            columns={columns}
            dataSource={tableData}
            onFilter={handleChangeTableFilter}
          />
        )}
        <Paginator
          page={Math.ceil(paginationData.offset + 1)}
          count={MFTasksList?.total || 0}
          onChange={(res) => setPaginationData(res)}
          limit={paginationData.limit}
        />
      </div>
      <RemovalModal
        onClose={() => setMFTaskIdForDeleted(undefined)}
        show={!!mFTaskIdForDeleted}
        handleDelete={handleApproveDeleteTask}
        description={t('delete_monitoring_text')}
      />
    </div>
  );
};

export default QmManageMonitoringTasks;
